import HealthReportActions from './HealthReportActions';
import { scoreBand } from '../lib/healthScore';
import { vehicleTitle } from '../lib/format';

const SCORE_FIELDS = [
  'engine_score',
  'braking_score',
  'tires_score',
  'battery_score',
  'cooling_score',
  'suspension_score',
  'electrical_score',
];

const SCORE_LABELS = {
  engine_score: 'Engine',
  braking_score: 'Braking',
  tires_score: 'Tires',
  battery_score: 'Battery',
  cooling_score: 'Cooling',
  suspension_score: 'Suspension',
  electrical_score: 'Electrical',
};

function DetailBlock({ title, body }) {
  if (!body?.trim()) return null;
  return (
    <div className="health-finalized-detail">
      <span>{title}</span>
      <p>{body.trim()}</p>
    </div>
  );
}

export default function HealthFinalizedView({
  report,
  vehicle,
  booking,
  onEdit,
  busy,
}) {
  const band = scoreBand(report.overall_score);
  const services = (report.overdue_services || [])
    .filter((row) => row?.service)
    .map((row) => (row.notes ? `${row.service} — ${row.notes}` : row.service))
    .join('\n');
  const conditions = [
    report.tire_condition ? `Tires: ${report.tire_condition}` : null,
    report.battery_condition ? `Battery: ${report.battery_condition}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const photoCount = Array.isArray(report.inspection_photos)
    ? report.inspection_photos.length
    : 0;

  return (
    <div className="health-finalized">
      <div className="health-finalized-banner">
        <span className="material-symbol">verified</span>
        <div>
          <strong>Inspection complete</strong>
          <p>
            Finalized{' '}
            {report.finalized_at
              ? new Date(report.finalized_at).toLocaleString()
              : 'recently'}
            {report.mileage ? ` · ${Number(report.mileage).toLocaleString()} km` : ''}
          </p>
        </div>
      </div>

      <div className={`health-finalized-hero health-band-${band}`}>
        <div className="health-finalized-score-ring" aria-hidden="true">
          <svg viewBox="0 0 88 88">
            <circle cx="44" cy="44" r="36" className="health-ring-track" />
            <circle
              cx="44"
              cy="44"
              r="36"
              className="health-ring-fill"
              style={{
                strokeDasharray: `${(report.overall_score / 100) * 226} 226`,
              }}
            />
          </svg>
          <strong>{report.overall_score}</strong>
        </div>
        <div className="health-finalized-hero-copy">
          <span className="kicker">Overall health</span>
          <h3>{vehicleTitle(vehicle)}</h3>
          <p>
            {[vehicle.plate_number, booking?.service_type].filter(Boolean).join(' · ') ||
              'Service visit'}
          </p>
          <div className="health-finalized-tags">
            <span className={`health-status-tag health-band-${band}`}>
              {band === 'good' ? 'Good condition' : band === 'fair' ? 'Needs attention' : 'Critical'}
            </span>
            {photoCount > 0 ? (
              <span className="health-status-tag soft">{photoCount} photo{photoCount === 1 ? '' : 's'}</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="health-finalized-scores">
        {SCORE_FIELDS.map((field) => (
          <div
            key={field}
            className={`health-finalized-score-pill health-band-${scoreBand(report[field])}`}
          >
            <span>{SCORE_LABELS[field]}</span>
            <strong>{report[field]}</strong>
          </div>
        ))}
      </div>

      <div className="health-finalized-details">
        <DetailBlock title="Overdue / recommended services" body={services} />
        <DetailBlock title="Diagnostics & warning lights" body={report.diagnostic_findings} />
        <DetailBlock title="Accident / repair notes" body={report.accident_repair_notes} />
        <DetailBlock title="Condition checks" body={conditions} />
        <DetailBlock
          title="Maintenance notes"
          body={report.maintenance_history_notes}
        />
      </div>

      <div className="health-finalized-actions">
        <button type="button" className="ghost" disabled={busy} onClick={onEdit}>
          <span className="material-symbol">edit</span>
          {busy ? 'Opening…' : 'Edit report'}
        </button>
        <HealthReportActions
          report={report}
          vehicle={vehicle}
          booking={booking}
          customer={booking?.profiles}
          className="quote-toolbar compact-toolbar"
        />
      </div>
    </div>
  );
}
